import { useCallback, useMemo, useState } from 'react';

export const SIDEBAR_EXPANDED_STORAGE_KEY = 'ui.sidebar.expanded@v1';

const defaultExpanded = true;

export type SidebarExpandedStorage = Pick<Storage, 'getItem' | 'setItem'>;

function toSidebarExpandedStorage(value: unknown): SidebarExpandedStorage | null {
  if (!value || typeof value !== 'object') return null;
  const maybe = value as { getItem?: unknown; setItem?: unknown };
  if (typeof maybe.getItem !== 'function') return null;
  if (typeof maybe.setItem !== 'function') return null;
  return value as SidebarExpandedStorage;
}

export function getSidebarExpandedFromStorage(storage: SidebarExpandedStorage | null): boolean {
  if (!storage) return defaultExpanded;

  try {
    const raw = storage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY);
    if (raw === null) return defaultExpanded;
    if (raw !== 'true' && raw !== 'false') return defaultExpanded;
    return raw === 'true';
  } catch {
    return defaultExpanded;
  }
}

export function setSidebarExpandedInStorage(
  storage: SidebarExpandedStorage | null,
  value: boolean
): void {
  if (!storage) return;

  try {
    storage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, String(value));
  } catch {
    // best-effort
  }
}

export function useSidebarExpanded(storageOverride?: SidebarExpandedStorage | null) {
  const storage = useMemo(() => {
    if (storageOverride !== undefined) return storageOverride;
    if (typeof window === 'undefined') return null;
    return toSidebarExpandedStorage(window.localStorage);
  }, [storageOverride]);

  const [isExpanded, setIsExpanded] = useState<boolean>(() =>
    getSidebarExpandedFromStorage(storage)
  );

  const setExpanded = useCallback(
    (value: boolean) => {
      setIsExpanded(value);
      setSidebarExpandedInStorage(storage, value);
    },
    [storage]
  );

  const toggle = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev;
      setSidebarExpandedInStorage(storage, next);
      return next;
    });
  }, [storage]);

  return {
    isExpanded,
    setExpanded,
    toggle,
  };
}
